// Validation lab: does the measurement work for *your* setup? Record
// deliberate mistakes, label what really happened, and see agreement,
// coverage and the errors that matter most.

import { useEffect, useMemo, useState } from 'react';
import type { Outcome } from '../../core/analysis';
import { TRUTH_SHORT, TRUTH_TEXT, validationReport, type TruthLabel } from '../../core/stats';
import { db, type AttemptRecord, type ModelVariant } from '../../engine/db';
import { rescoreAll } from '../../engine/offline';
import { navigate, useApp } from '../app-state';
import { fmtPct, Notice, PageHead } from '../components/bits';
import { Icon } from '../components/Icon';

const OUTCOME_SHORT: Record<Outcome, string> = { clean: 'Clean', adjust: 'Adjust', unassessed: 'Not assessed', none: 'No pop-up' };

export function Lab() {
  const app = useApp();
  const [attempts, setAttempts] = useState<AttemptRecord[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => setAttempts(await db.allAttempts());
  useEffect(() => {
    void load();
  }, []);

  const labelled = useMemo(() => (attempts ?? []).filter((a) => a.label), [attempts]);
  const report = useMemo(() => validationReport(labelled.map((a) => ({ label: a.label!, analysis: a.analysis }))), [labelled]);
  const models = useMemo(() => {
    const out: { model: ModelVariant; n: number; agree: number; assessed: number }[] = [];
    for (const m of ['lite', 'full', 'heavy'] as const) {
      const items = labelled.filter((a) => a.alt?.[m]).map((a) => ({ label: a.label!, analysis: a.alt![m]!.analysis }));
      if (!items.length) continue;
      const r = validationReport(items);
      out.push({ model: m, n: items.length, agree: r.agreement ?? 0, assessed: r.coverage ?? 0 });
    }
    return out;
  }, [labelled]);

  const rescore = async () => {
    setBusy('Re-scoring…');
    const n = await rescoreAll(app.settings, (d, t) => setBusy(`Re-scoring ${d}/${t}…`));
    await load();
    setBusy(null);
    app.toast(`Re-scored ${n} attempts with current settings`);
  };

  // Two steps: build the file, then share it from a fresh tap (iOS refuses
  // share() once awaits have broken the original gesture).
  const [exportFile, setExportFile] = useState<File | null>(null);
  const prepareExport = async () => {
    setBusy('Preparing export…');
    const [sessions, cals, all] = await Promise.all([db.listSessions(), db.listCalibrations(), db.allAttempts()]);
    const plain = (x: unknown): unknown =>
      x instanceof Float32Array || x instanceof Uint8Array ? Array.from(x) : Array.isArray(x) ? x.map(plain) : x && typeof x === 'object' ? Object.fromEntries(Object.entries(x).map(([k, v]) => [k, plain(v)])) : x;
    const data = {
      app: 'surfdaddy',
      exportedAt: new Date().toISOString(),
      frameLayout: 'per frame: [t_ms, present, 33 × (x, y, visibility)] — MediaPipe Pose landmark order',
      calibrations: cals.map((c) => plain({ ...c, anchors: undefined })),
      sessions,
      attempts: all.map((a) => plain({ ...a, analysis: { ...a.analysis, series: undefined } })),
    };
    setExportFile(new File([JSON.stringify(data)], `surfdaddy-${new Date().toISOString().slice(0, 10)}.json`, { type: 'application/json' }));
    setBusy(null);
  };
  const shareExport = () => {
    if (!exportFile) return;
    if (navigator.canShare?.({ files: [exportFile] })) {
      void navigator.share({ files: [exportFile], title: 'SurfDaddy data' }).catch(() => {});
      return;
    }
    const url = URL.createObjectURL(exportFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFile.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const outcomes: Outcome[] = ['clean', 'adjust', 'unassessed', 'none'];
  const truths = (Object.keys(TRUTH_TEXT) as TruthLabel[]).filter((t) => outcomes.some((o) => report.confusion[t][o] > 0));

  return (
    <div className="page stack lg">
      <PageHead title="Validation lab" />
      <div className="card stack">
        <p className="muted">
          Before trusting the feedback, check it on your own pop-ups. A validation set asks you to land correctly and deliberately wrong — short, long, off-centre,
          narrow, wide, shuffling afterwards — and pre-labels each attempt. Review the clips, correct any label, and the numbers below show how often the app is right
          and how often it declines to judge.
        </p>
        <button className="btn primary block" disabled={!app.active} onClick={() => navigate('practice?mode=experiment')}>
          <Icon name="flask" /> Start a validation set
        </button>
        {!app.active && <p className="small faint">Complete setup first.</p>}
      </div>

      <div>
        <div className="section-title">Results ({report.labelled} labelled attempts)</div>
        {report.labelled === 0 ? (
          <p className="muted small">Label attempts (or run a validation set) to see results.</p>
        ) : (
          <div className="stack">
            <div className="tiles">
              <div className="tile">
                <div className="label">Verdict matches label</div>
                <div className="value">{fmtPct(report.agreement)}</div>
                <div className="sub">all labelled attempts</div>
              </div>
              <div className="tile">
                <div className="label">Coverage</div>
                <div className="value">{fmtPct(report.coverage)}</div>
                <div className="sub">real attempts given a verdict</div>
              </div>
              <div className="tile">
                <div className="label">Right when it judged</div>
                <div className="value">{fmtPct(report.accuracyWhenAssessed)}</div>
                <div className="sub">clean vs needs adjustment</div>
              </div>
              <div className="tile">
                <div className="label">Blamed the right foot</div>
                <div className="value">{fmtPct(report.rightFoot)}</div>
                <div className="sub">off-target landings</div>
              </div>
            </div>
            {report.correctedScoredClean > 0 ? (
              <Notice kind="warn">
                {report.correctedScoredClean} shuffled landing(s) were scored clean — the error the app must avoid. Check those clips: was the step visible?
              </Notice>
            ) : (
              <Notice kind="good">No shuffled landing has been scored clean.</Notice>
            )}
            <div className="card flush" style={{ overflowX: 'auto' }}>
              <table className="table">
                <caption className="small faint" style={{ textAlign: 'left', padding: '10px 10px 0' }}>
                  Rows: what happened · columns: what the app said
                </caption>
                <thead>
                  <tr>
                    <th>Happened</th>
                    {outcomes.map((o) => (
                      <th key={o}>{OUTCOME_SHORT[o]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {truths.map((t) => (
                    <tr key={t}>
                      <td title={TRUTH_TEXT[t]}>{TRUTH_SHORT[t]}</td>
                      {outcomes.map((o) => (
                        <td key={o} style={{ fontWeight: report.confusion[t][o] ? 650 : 400, color: report.confusion[t][o] ? 'var(--text)' : 'var(--text-3)' }}>
                          {report.confusion[t][o]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {models.length > 0 && (
        <div>
          <div className="section-title">Model comparison (frame-by-frame re-analysis)</div>
          <div className="card flush">
            <table className="table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Attempts</th>
                  <th>Agreement</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.model}>
                    <td>{m.model}</td>
                    <td>{m.n}</td>
                    <td>{fmtPct(m.agree)}</td>
                    <td>{fmtPct(m.assessed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="tiny faint" style={{ marginTop: 6 }}>Re-analyse attempts from their detail page. Pick the lightest model whose agreement holds up.</p>
        </div>
      )}

      <div>
        <div className="section-title">Tools</div>
        <div className="card flush list">
          <button className="list-item" onClick={rescore} disabled={!!busy}>
            <Icon name="refresh" />
            <div className="grow">
              <div>Re-score all attempts</div>
              <div className="small faint">Apply current strictness and hold settings to your history.</div>
            </div>
          </button>
          <button className="list-item" onClick={exportFile ? shareExport : prepareExport} disabled={!!busy}>
            <Icon name={exportFile ? 'share' : 'download'} />
            <div className="grow">
              <div>{exportFile ? `Share ${exportFile.name} (${(exportFile.size / 1e6).toFixed(1)} MB)` : 'Export data (JSON)'}</div>
              <div className="small faint">Landmark timelines, verdicts and labels — no video.</div>
            </div>
          </button>
        </div>
        {busy && (
          <div className="row" style={{ marginTop: 10 }}>
            <div className="spinner" /> <span className="small muted">{busy}</span>
          </div>
        )}
      </div>
    </div>
  );
}
